import type { IObjectSchema } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import { DiagnosticReportValidationTypes, ProcedureValidationTypes } from '@common/enums';
import type { IAllergies } from './allergy';
import type { IAssets } from './assets';
import type { IDiagnosisAddEditValidated } from './diagnosis';
import type { IEpisodesAndJournals } from './episodesAndJournal';
import type { ILabResults } from './labResult';
import type { Medications } from './medication';
import type { ISchemaContainer } from './schemaContainer';
import { allergies } from './allergy';
import { assets } from './assets';
import {
    tcohV2DraftProcedureWithRequest,
    tcohV2FinalProcedureWithRequest,
    tcorV1DraftProcedure,
    tcorV1DraftProcedureWithRequest,
    tcorV1FinalProcedure,
    tcorV1FinalProcedureWithRequest,
    tiecgV1DraftProcedure,
    tiecgV1FinalProcedure,
} from './customControls/cardiology/holterConsult/cardiology';
import { altoDermaOthFinalProcedure, dermaOthFinalProcedure } from './customControls/dermatology';
import { gtoFinalProcedure } from './customControls/general';
import {
    orthOFolV1DraftProcedure,
    orthOFolV1FinalProcedure,
    orthOV1DraftProcedure,
    orthOV1FinalProcedure,
    orthOV1FinalProcedureWithRequest,
    tfoV1DraftProcedure,
    tfoV1FinalProcedure,
    tooV1DraftProcedure,
    tooV1FinalProcedure,
    tooV1FinalProcedureWithRequest,
} from './customControls/ophthalmology';
import { oorkV1DraftProcedure, oorkV1FinalProcedure } from './customControls/ophthalmology/oork';
import { pilotExaminationV1FinalProcedure } from './customControls/pilot';
import { tpoV1DraftProcedure, tpoV1FinalProcedure } from './customControls/pulmonology';
import { diagnosticReportBase, getDiagnosticReportSchemas } from './diagnosticReport';
import { episodesAndJournalEntriesSchema } from './episodesAndJournal';
import { labResults } from './labResult';
import { medications } from './medication';

const emptyObjectSchema = Koi.object({});

export interface IProcedureEditPerformerValidated {
    performerOrganizationId: string;
    performerPractitionerId: string;
}

const procedureEditPerformer = Koi.object().keys({
    performerOrganizationId: Koi.string().uuid(),
    performerPractitionerId: Koi.string().uuid(),
});

export const procedureEditPerformerSchema: ISchemaContainer<IProcedureEditPerformerValidated> = {
    schema: procedureEditPerformer.options({ convert: false, presence: 'required' }),
};

export interface IProcedureUpdateOneCustomControlDataValidated {
    customControlData: {
        addedAnalysisReportDatetime?: string;
    };
}

const procedureUpdateOneCustomControlData = Koi.object({
    customControlData: Koi.object({
        addedAnalysisReportDatetime: Koi.string().isoDate().optional(),
    }),
});

// eslint-disable-next-line @stylistic/max-len
export const procedureUpdateOneCustomControlDataSchema: ISchemaContainer<IProcedureUpdateOneCustomControlDataValidated> =
    {
        schema: procedureUpdateOneCustomControlData.options({ convert: false, presence: 'required' }),
    };

export interface IProcedureRequestValidated {
    customControlData: any;
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
    id?: string;
    patientId: string;
    requesterOrganizationId: string | null;
    requesterPractitionerId: string | null;
    serviceId: string;
}

export interface IProcedureValidated {
    customControlData: any;
    id?: string;
    patientId: string;
    patientUsesMedication: boolean | null;
    remark: string | null;
    requestId?: string;
    selectedDiagnosticReportProviderOrganizationId: string | null;
    selectedDiagnosticReportProviderPractitionerId: string | null;
}

export interface IProcedureDiagnosticReportValidated {
    customControlData: any;
    diagnosis: IDiagnosisAddEditValidated;
    id?: string;
    patientId: string;
    requestId?: string;
}

export interface ISaveAndSaveAndSendValidated {
    allergies?: IAllergies;
    assets: IAssets;
    diagnosticReport: IProcedureDiagnosticReportValidated | null;
    episodesAndJournals?: IEpisodesAndJournals;
    labResults?: ILabResults;
    medications?: Medications;
    procedure: IProcedureValidated;
    request: IProcedureRequestValidated;
    validationType: ProcedureValidationTypes;
}

const procedure = Koi.object({
    id: Koi.string().uuid().optional(),
    patientId: Koi.string().uuid(),
    patientUsesMedication: Koi.boolean().optional().allow(null),
    remark: Koi.string().max(1000).allow(null),
    requestId: Koi.string().uuid().optional(),
    selectedDiagnosticReportProviderOrganizationId: Koi.string().uuid().allow(null),
    selectedDiagnosticReportProviderPractitionerId: Koi.string().uuid().allow(null),
});

const request = Koi.object({
    draftCareProviderOrganizationId: Koi.string().uuid().allow(null),
    draftCareProviderPractitionerId: Koi.string().uuid().allow(null),
    hasCostConsentPatient: Koi.boolean().optional().allow(null),
    id: Koi.string().uuid().optional(),
    patientId: Koi.string().uuid(),
    requesterOrganizationId: Koi.string().uuid().allow(null),
    requesterPractitionerId: Koi.string().uuid().allow(null),
    serviceId: Koi.string().uuid(),
});

export function getSaveAndSaveAndSendValidationSchema(
    procedureValidationType: ProcedureValidationTypes
): ISchemaContainer<ISaveAndSaveAndSendValidated> {
    return {
        schema: getProcedureSchema(procedureValidationType)
            .concat(
                Koi.object({
                    allergies: allergies.optional(),
                    assets: assets,
                    episodesAndJournals: episodesAndJournalEntriesSchema.optional(),
                    labResults: labResults.optional(),
                    medications: medications.optional(),
                    validationType: Koi.koi().enum(ProcedureValidationTypes).disallow(null),
                })
            )
            .options({ convert: false, presence: 'required' }),
    };
}

export function getProcedureValidationSchema(
    procedureValidationType: ProcedureValidationTypes
): ISchemaContainer<IProcedureValidated> {
    return {
        schema: getProcedureSchema(procedureValidationType).options({ convert: false, presence: 'required' }),
    };
}

function getProcedureSchema(procedureValidationType: ProcedureValidationTypes) {
    const { procedureCustomControlSchema, diagnosticReportSchema } = getSchemas(procedureValidationType);

    return Koi.object({
        diagnosticReport: diagnosticReportSchema
            ? diagnosticReportBase
                  .concat(
                      Koi.object({
                          customControlData: diagnosticReportSchema.customControlSchema.disallow(null),
                          diagnosis: diagnosticReportSchema.diagnosisSchema.disallow(null),
                      })
                  )
                  .concat(diagnosticReportSchema.baseSchemaExistention)
                  .allow(null)
            : Koi.valid(null),
        procedure: procedureCustomControlSchema
            ? procedure.concat(Koi.object({ customControlData: procedureCustomControlSchema.disallow(null) }))
            : procedure.concat(Koi.object({ customControlData: Koi.valid(null) })),
        request: procedureValidationType.includes('Final')
            ? request.concat(Koi.object({ requesterOrganizationId: Koi.any().disallow(null) }))
            : request,
    });
}

export function getSchemas(procedureValidationType: ProcedureValidationTypes) {
    let procedureCustomControlSchema: IObjectSchema;
    let diagnosticReportSchema: ReturnType<typeof getDiagnosticReportSchemas> | null = null;
    switch (procedureValidationType) {
        case ProcedureValidationTypes.osasoV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasoV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.osasoV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasoV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.osasoV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasoV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.osasoV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasoV1EditFinalDiagnosticReport
            );
            break;

        case ProcedureValidationTypes.osasvoV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasvoV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.osasvoV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasvoV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.osasvoV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasvoV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.osasvoV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.osasvoV1EditFinalDiagnosticReport
            );
            break;

        case ProcedureValidationTypes.osasqV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.osasqV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.osasqV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.osasqV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;

        case ProcedureValidationTypes.tpoV1AddDraftProcedure:
            procedureCustomControlSchema = tpoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tpoV1AddFinalProcedure:
            procedureCustomControlSchema = tpoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tpoV1AddDraftProcedureWithRequest:
            procedureCustomControlSchema = tpoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tpoV1AddFinalProcedureWithRequest:
            procedureCustomControlSchema = tpoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tpoV1EditDraftProcedure:
            procedureCustomControlSchema = tpoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tpoV1EditFinalProcedure:
            procedureCustomControlSchema = tpoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tpoV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tpoV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tpoV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tpoV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tpoV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tpoV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tpoV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tpoV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tpoV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tpoV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tpoV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tpoV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tpoV1AddDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tpoV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tpoV1AddDraftDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.tpoV1AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tpoV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tpoV1AddFinalDiagnosticReportWithRequest
            );
            break;

        case ProcedureValidationTypes.tcorV1AddDraftProcedure:
            procedureCustomControlSchema = tcorV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tcorV1EditDraftProcedure:
            procedureCustomControlSchema = tcorV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tcorV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tcorV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tcorV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tcorV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tcorV1AddFinalProcedure:
            procedureCustomControlSchema = tcorV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tcorV1EditFinalProcedure:
            procedureCustomControlSchema = tcorV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tcorV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tcorV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tcorV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tcorV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tcorV1AddDraftProcedureWithRequest:
            procedureCustomControlSchema = tcorV1DraftProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcorV1EditDraftProcedureWithRequest:
            procedureCustomControlSchema = tcorV1DraftProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcorV1AddDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcorV1DraftProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1AddDraftDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.tcorV1EditDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcorV1DraftProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tcorV1AddFinalProcedureWithRequest:
            procedureCustomControlSchema = tcorV1FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcorV1EditFinalProcedureWithRequest:
            procedureCustomControlSchema = tcorV1FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcorV1AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcorV1FinalProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1AddFinalDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.tcorV1EditFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcorV1FinalProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tcorV1EditFinalDiagnosticReport
            );
            break;

        case ProcedureValidationTypes.tcohV1AddDraftProcedure:
        case ProcedureValidationTypes.tcohV2AddDraftProcedure:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1AddFinalProcedure:
        case ProcedureValidationTypes.tcohV2AddFinalProcedure:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1EditDraftProcedure:
        case ProcedureValidationTypes.tcohV2EditDraftProcedure:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1EditFinalProcedure:
        case ProcedureValidationTypes.tcohV2EditFinalProcedure:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1AddDraftProcedureWithRequest:
        case ProcedureValidationTypes.tcohV2AddDraftProcedureWithRequest:
            procedureCustomControlSchema = tcohV2DraftProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1AddFinalProcedureWithRequest:
        case ProcedureValidationTypes.tcohV2AddFinalProcedureWithRequest:
            procedureCustomControlSchema = tcohV2FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1EditDraftProcedureWithRequest:
        case ProcedureValidationTypes.tcohV2EditDraftProcedureWithRequest:
            procedureCustomControlSchema = tcohV2DraftProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1EditFinalProcedureWithRequest:
        case ProcedureValidationTypes.tcohV2EditFinalProcedureWithRequest:
            procedureCustomControlSchema = tcohV2FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1AddDraftProcedureDiagnosticReport:
        case ProcedureValidationTypes.tcohV2AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1AddFinalProcedureDiagnosticReport:
        case ProcedureValidationTypes.tcohV2AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1EditDraftProcedureDiagnosticReport:
        case ProcedureValidationTypes.tcohV2EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1EditFinalProcedureDiagnosticReport:
        case ProcedureValidationTypes.tcohV2EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = emptyObjectSchema;
            break;
        case ProcedureValidationTypes.tcohV1AddDraftProcedureDiagnosticReportWithRequest:
        case ProcedureValidationTypes.tcohV2AddDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcohV2DraftProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1AddFinalProcedureDiagnosticReportWithRequest:
        case ProcedureValidationTypes.tcohV2AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcohV2FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1EditFinalProcedureDiagnosticReportWithRequest:
        case ProcedureValidationTypes.tcohV2EditFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcohV2FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tcohV1EditDraftProcedureDiagnosticReportWithRequest:
        case ProcedureValidationTypes.tcohV2EditDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tcohV2DraftProcedureWithRequest;
            break;

        case ProcedureValidationTypes.tiecgV1AddDraftProcedure:
            procedureCustomControlSchema = tiecgV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tiecgV1AddFinalProcedure:
            procedureCustomControlSchema = tiecgV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tiecgV1AddDraftProcedureWithRequest:
            procedureCustomControlSchema = tiecgV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tiecgV1AddFinalProcedureWithRequest:
            procedureCustomControlSchema = tiecgV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tiecgV1EditDraftProcedure:
            procedureCustomControlSchema = tiecgV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tiecgV1EditFinalProcedure:
            procedureCustomControlSchema = tiecgV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tiecgV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tiecgV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tiecgV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tiecgV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tiecgV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tiecgV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tiecgV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tiecgV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tiecgV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tiecgV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tiecgV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tiecgV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tiecgV1AddDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tiecgV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tiecgV1AddDraftDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.tiecgV1AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tiecgV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tiecgV1AddFinalDiagnosticReportWithRequest
            );
            break;

        case ProcedureValidationTypes.tfoV1AddDraftProcedure:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tfoV1AddFinalProcedure:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tfoV1EditDraftProcedure:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tfoV1EditFinalProcedure:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tfoV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tfoV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tfoV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tfoV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tfoV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tfoV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tfoV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tfoV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tfoV1AddDraftProcedureWithRequest:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tfoV1AddFinalProcedureWithRequest:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tfoV1EditDraftProcedureWithRequest:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tfoV1EditFinalProcedureWithRequest:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tfoV1AddDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tfoV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tfoV1AddDraftDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.tfoV1AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tfoV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tfoV1AddFinalDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.tooV1AddDraftProcedure:
            procedureCustomControlSchema = tooV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tooV1AddFinalProcedure:
            procedureCustomControlSchema = tooV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tooV1EditDraftProcedure:
            procedureCustomControlSchema = tooV1DraftProcedure;
            break;
        case ProcedureValidationTypes.tooV1EditFinalProcedure:
            procedureCustomControlSchema = tooV1FinalProcedure;
            break;
        case ProcedureValidationTypes.tooV1AddFinalProcedureWithRequest:
        case ProcedureValidationTypes.tooV1EditFinalProcedureWithRequest:
            procedureCustomControlSchema = tooV1FinalProcedureWithRequest;
            break;
        case ProcedureValidationTypes.tooV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tooV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tooV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tooV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tooV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tooV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tooV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = tooV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tooV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tooV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = tooV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tooV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.tooV1AddFinalProcedureDiagnosticReportWithRequest:
        case ProcedureValidationTypes.tooV1EditFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = tooV1FinalProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.tooV1AddFinalDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddDraftProcedure:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddFinalProcedure:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddDraftProcedureWithRequest:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddFinalProcedureWithRequest:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            break;
        case ProcedureValidationTypes.pilotExaminationV1EditDraftProcedure:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            break;
        case ProcedureValidationTypes.pilotExaminationV1EditFinalProcedure:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.pilotExaminationV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.pilotExaminationV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.pilotExaminationV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.pilotExaminationV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.pilotExaminationV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.pilotExaminationV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddDraftProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.pilotExaminationV1AddDraftDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.pilotExaminationV1AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = pilotExaminationV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.pilotExaminationV1AddFinalDiagnosticReportWithRequest
            );
            break;
        case ProcedureValidationTypes.gtoV1AddFinalProcedure:
            procedureCustomControlSchema = gtoFinalProcedure;
            break;
        case ProcedureValidationTypes.dermaOthV1AddFinalProcedure:
            procedureCustomControlSchema = dermaOthFinalProcedure;
            break;
        case ProcedureValidationTypes.altoDermaOthV1AddFinalProcedure:
            procedureCustomControlSchema = altoDermaOthFinalProcedure;
            break;
        case ProcedureValidationTypes.orthOV1AddDraftProcedure:
            procedureCustomControlSchema = orthOV1DraftProcedure;
            break;
        case ProcedureValidationTypes.orthOV1EditDraftProcedure:
            procedureCustomControlSchema = orthOV1DraftProcedure;
            break;
        case ProcedureValidationTypes.orthOV1AddFinalProcedure:
            procedureCustomControlSchema = orthOV1FinalProcedure;
            break;
        case ProcedureValidationTypes.orthOV1EditFinalProcedure:
            procedureCustomControlSchema = orthOV1FinalProcedure;
            break;
        case ProcedureValidationTypes.orthOV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOV1EditFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = orthOV1FinalProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOV1AddFinalProcedureDiagnosticReportWithRequest:
            procedureCustomControlSchema = orthOV1FinalProcedureWithRequest;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOFolV1AddDraftProcedure:
            procedureCustomControlSchema = orthOFolV1DraftProcedure;
            break;
        case ProcedureValidationTypes.orthOFolV1AddFinalProcedure:
            procedureCustomControlSchema = orthOFolV1FinalProcedure;
            break;
        case ProcedureValidationTypes.orthOFolV1EditDraftProcedure:
            procedureCustomControlSchema = orthOFolV1DraftProcedure;
            break;
        case ProcedureValidationTypes.orthOFolV1EditFinalProcedure:
            procedureCustomControlSchema = orthOFolV1FinalProcedure;
            break;
        case ProcedureValidationTypes.orthOFolV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOFolV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOFolV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOFolV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOFolV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOFolV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOFolV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOFolV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOFolV1AddFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.orthOFolV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = orthOFolV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.orthOFolV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.oorkV1EditDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = oorkV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.oorkV1EditDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.oorkV1AddDraftProcedureDiagnosticReport:
            procedureCustomControlSchema = oorkV1DraftProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.oorkV1AddDraftDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.oorkV1EditFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = oorkV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.oorkV1EditFinalDiagnosticReport
            );
            break;
        case ProcedureValidationTypes.oorkV1AddFinalProcedureDiagnosticReport:
            procedureCustomControlSchema = oorkV1FinalProcedure;
            diagnosticReportSchema = getDiagnosticReportSchemas(
                DiagnosticReportValidationTypes.oorkV1AddFinalDiagnosticReport
            );
            break;
        default:
            throw new Error('Unable to find procedure validation schema');
    }

    return { diagnosticReportSchema, procedureCustomControlSchema };
}
